if (!customElements.get('product-info-tabs')) {
  customElements.define(
    'product-info-tabs',
    class ProductInfoTabs extends HTMLElement {
      connectedCallback() {
        this.nav = this.querySelector('.ptabs__nav');
        this.tabs = Array.from(this.querySelectorAll('.ptabs__tab'));
        this.panels = Array.from(this.querySelectorAll('.ptabs__panel'));

        if (!this.nav || this.tabs.length === 0) return;

        this.activeIndex = Math.max(
          0,
          this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
        );

        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => this.select(index));
          tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
        });

        this.onResize = this.syncUnderline.bind(this);

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.onResize);
          this.resizeObserver.observe(this.nav);
        } else {
          window.addEventListener('resize', this.onResize);
        }

        // Fonts can land after first paint and shift tab widths.
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(this.onResize).catch(() => {});
        }

        this.syncUnderline();

        // Skip the slide animation on the very first positioning.
        requestAnimationFrame(() => {
          this.nav.dataset.ready = 'true';
        });
      }

      disconnectedCallback() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.onResize) window.removeEventListener('resize', this.onResize);
      }

      select(index, { focus = false } = {}) {
        if (index < 0 || index >= this.tabs.length) return;

        this.tabs.forEach((tab, i) => {
          const isActive = i === index;
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
          tab.setAttribute('tabindex', isActive ? '0' : '-1');
        });

        this.panels.forEach((panel, i) => {
          panel.hidden = i !== index;
        });

        this.activeIndex = index;
        this.syncUnderline();

        if (focus) this.tabs[index].focus();

        this.dispatchEvent(
          new CustomEvent('tabChange', {
            bubbles: true,
            detail: { selectedIndex: index, selectedTab: this.panels[index] }
          })
        );
      }

      onKeydown(event, index) {
        const lastIndex = this.tabs.length - 1;
        const isRtl = getComputedStyle(this).direction === 'rtl';
        let next = null;

        switch (event.key) {
          case 'ArrowRight':
            next = isRtl ? index - 1 : index + 1;
            break;
          case 'ArrowLeft':
            next = isRtl ? index + 1 : index - 1;
            break;
          case 'Home':
            next = 0;
            break;
          case 'End':
            next = lastIndex;
            break;
          default:
            return;
        }

        if (next < 0) next = lastIndex;
        if (next > lastIndex) next = 0;

        event.preventDefault();
        this.select(next, { focus: true });
      }

      syncUnderline() {
        if (this.dataset.layout !== 'underline') return;

        const tab = this.tabs[this.activeIndex];
        if (!tab) return;

        // The indicator is pinned to the inline-start edge, but translateX is always
        // physical — so in RTL it has to travel leftwards from the right edge.
        const isRtl = getComputedStyle(this).direction === 'rtl';
        const offset = isRtl ? tab.offsetLeft + tab.offsetWidth - this.nav.offsetWidth : tab.offsetLeft;

        this.nav.style.setProperty('--ptabs-underline-width', `${tab.offsetWidth}px`);
        this.nav.style.setProperty('--ptabs-underline-offset', `${offset}px`);
        this.nav.dataset.underlineVisible = 'true';
      }
    }
  );
}
